import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Button, Grid } from "@mui/material";
import ControlPointOutlinedIcon from "@mui/icons-material/ControlPointOutlined";
import useWarehouseService from "../../../../services/warehouseService";
import { useAsync } from "../../../../hooks/useAsync";
import usePaginationWithSearchParams from "../../../../hooks/usePaginationWithSearchParams";
import { centerVericalAlignIconStyle } from "../../../../helpers/styles";
import TransactionTable from "../../../table/TransactionTable";
import { getTableConfigByItemType } from "./TableConfig";
import TransactionDialog from "../../../dialog/TransactionDialog";
import { TRANSACTION_KIND_MOVE } from "../../../../helpers/constants";
import TransactionMoveForm from "../../../form/TransactionMoveForm";
import { transactionMoveExportDataUrl } from "../../../../helpers/apiUrls";
import { isEmptyObject } from "../../../../helpers/methods";

const WarehouseTransactionMoveTab = forwardRef((props, ref) => {
  const { t } = useTranslation();

  const tableConfig = useMemo(() => {
    return getTableConfigByItemType(props.itemType);
  }, [props.itemType]);

  const [hasPermissionToEdit, setHasPermissionToEdit] = useState(true);

  const {
    page: transactionMovePage,
    pageSize: transactionMovePageSize,
    handleChangePageWithSearchParams: handleChangeTransactionMoveDataPage,
    handleChangeRowsPerPage,
    resetPageNumber,
    searchParams,
    setSearchParams,
  } = usePaginationWithSearchParams();

  const { getTransactionMoveData } = useWarehouseService();

  useEffect(() => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (props.itemType === "equipment") {
      newSearchParams.set('equipment__isnull', false);
      setSearchParams(newSearchParams)
    } else {
      newSearchParams.set('part__isnull', false);
    }
    setSearchParams(newSearchParams)
  }, [searchParams, props.itemType]);



  const transactionMoveList = useAsync(() => {
    const urlParams = new URLSearchParams(searchParams);
    const params = Object.fromEntries(urlParams.entries());
    if (!isEmptyObject(params)) {
      return getTransactionMoveData(searchParams);
    }
    return Promise.resolve([])
  }, [searchParams]);

  useImperativeHandle(ref, () => ({
    onRefetchData() {
      transactionMoveList.refetch();
    },
  }));

  return (
    <Grid container spacing={1}>
      {hasPermissionToEdit && (
        <Grid item xs={12} sm={2}>
          <Button
            fullWidth
            variant="contained"
            size="small"
            startIcon={
              <ControlPointOutlinedIcon sx={centerVericalAlignIconStyle} />
            }
            onClick={props.onOpenTransactionDialog}
          >
            {t("tabs.warehouse_transaction_tabs.new_transaction_move")}
          </Button>
        </Grid>
      )}
      <Grid item xs={12}>
        <TransactionTable
          transactionFilteringData={props.transactionFilteringData}
          tableConfig={tableConfig}
          transactionData={transactionMoveList ? transactionMoveList : []}
          page={transactionMovePage}
          pageSize={transactionMovePageSize}
          handleChangePageWithSearchParams={handleChangeTransactionMoveDataPage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          resetPageNumber={resetPageNumber}
          exportToFileUrl={transactionMoveExportDataUrl}
          exportToFileSearchParams={searchParams}
        />
      </Grid>
      {props.openTransactionDialog && (
        <TransactionDialog
          open={props.openTransactionDialog}
          title={t("tabs.warehouse_transaction_tabs.new_transaction_move")}
          onClose={props.onCloseTransactionDialog}
          transactionKind={TRANSACTION_KIND_MOVE}
          extraRequiredFields={["warehouse_receiving"]}
          onSubmit={props.onSubmit}
          isDataSaving={props.isSaving}
          transactionForm={<TransactionMoveForm itemType={props.itemType} />}
          itemType={props.itemType}
        />
      )}
    </Grid>
  );
});

WarehouseTransactionMoveTab.propTypes = {
  readOnly: PropTypes.bool,
  transactionType: PropTypes.string,
  itemType: PropTypes.string,
};

WarehouseTransactionMoveTab.defaultProps = {
  itemType: "part",
};

export default WarehouseTransactionMoveTab;
